import { useCallback, useMemo } from 'react';
import { StyleSheet } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { LineSeparator } from '@/components/LineSeparator';
import { Text } from '@/components/TextComponent';
import { ds } from '@/components/lib/design.system';
import { appTheme } from '@/components/lib/theme';
import { showScrollIndicator } from '@/utils/constants-platform-specific';
import { FlashList, ListRenderItemInfo } from '@shopify/flash-list';

import { AllLeaguesItem, AllLeaguesListItem } from '../utils/all-leagues-utils';
import { AllLeaguesRow } from './AllLeaguesRow';

type AllLeaguesListProps = {
    items: AllLeaguesListItem[];
    onSelectLeague: (league: AllLeaguesItem) => void;
};

const ROW_HEIGHT = 56;

const keyExtractor = (item: AllLeaguesListItem) => item.key;
const getItemType = (item: AllLeaguesListItem) => item.type;

export const AllLeaguesList = ({ items, onSelectLeague }: AllLeaguesListProps) => {
    const { bottom } = useSafeAreaInsets();

    const contentContainerStyle = useMemo(
        () => ({ paddingHorizontal: appTheme.spacing.s16, paddingBottom: bottom + appTheme.spacing.s24 }),
        [bottom]
    );

    const renderItem = useCallback(
        ({ item }: ListRenderItemInfo<AllLeaguesListItem>) => {
            switch (item.type) {
                case 'header':
                    return (
                        <Text pt={'s12'} pb={'s8'} variant={'titleMedium'} color={ds.colorToken.text.secondary}>
                            {item.label}
                        </Text>
                    );
                case 'league':
                    return <AllLeaguesRow league={item.league} onPress={onSelectLeague} />;
                case 'divider':
                    return <LineSeparator style={styles.separator} />;
            }
        },
        [onSelectLeague]
    );

    return (
        <FlashList
            data={items}
            renderItem={renderItem}
            keyExtractor={keyExtractor}
            getItemType={getItemType}
            estimatedItemSize={ROW_HEIGHT}
            showsVerticalScrollIndicator={showScrollIndicator}
            contentContainerStyle={contentContainerStyle}
        />
    );
};

const styles = StyleSheet.create({
    separator: {
        marginVertical: appTheme.spacing.s16,
    },
});
